"use client"

import { Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { FormItem, FormLabel, FormDescription, FormMessage, FormField } from "@/components/ui/form"
import { cn } from "@/lib/utils"
import type { FormElement } from "./types"
import type { UseFormReturn } from "react-hook-form"

interface FormFileUploadProps {
  element: FormElement
  form: UseFormReturn<any>
}

export function FormFileUpload({ element, form }: FormFileUploadProps) {
  return (
    <FormField
      control={form.control}
      name={element.name!}
      rules={{
        required: element.validation?.required ? `${element.ui?.label || element.name} is required` : false,
      }}
      render={({ field: { onChange, value, ...field } }) => {
        const files = value as FileList | undefined

        return (
          <FormItem>
            {element.ui?.label && <FormLabel>{element.ui.label}</FormLabel>}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer",
              )}
            >
              <input
                type="file"
                id={element.name}
                name={field.name}
                ref={field.ref}
                className="hidden"
                accept={element.attributes?.accept}
                onChange={(e) => onChange(e.target.files)}
                onBlur={field.onBlur}
              />
              <label htmlFor={element.name} className="cursor-pointer">
                <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {element.ui?.placeholder || "Click to upload or drag and drop"}
                </p>
                {element.attributes?.accept && (
                  <p className="text-xs text-muted-foreground mt-1">{element.attributes.accept}</p>
                )}
              </label>
            </div>
            {files && files.length > 0 && (
              <div className="space-y-2 mt-2">
                {Array.from(files).map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm truncate">{file.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {element.ui?.hint && <FormDescription>{element.ui.hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

